import {useFoundationTheme} from 'platform/foundation';

import {isNil} from 'ramda';

import type {Nullish} from 'shared/web';

import {getValueAxisTicks} from '../utils/getValueAxisTicks';
import {measureWidestText} from '../utils/measureWidestText';
import {useChartStyle} from './useChartStyle';

// The Recharts default width, kept for an axis whose ticks stay with Recharts.
const DEFAULT_AXIS_WIDTH = 60;

const NON_BREAKING_SPACE = ' ';

interface ValueAxisOptions {
  /** False keeps every step a whole number, for values such as counts. */
  allowDecimals?: boolean;
}

/**
 * Props for a value axis (`YAxis`). The axis is as wide as its widest tick label, so the labels
 * start at the card padding instead of floating in a fixed width. Pass every value the chart
 * draws, for a stacked chart the stack totals, so the ticks cover them all.
 */
export function useValueAxis(
  values: ReadonlyArray<number | Nullish>,
  formatTick: (value: number) => string,
  options?: ValueAxisOptions
) {
  const theme = useFoundationTheme();
  const chartStyle = useChartStyle();
  const ticks = getValueAxisTicks(values, options);
  // Recharts breaks a label at its spaces when it measures the label wider than the axis. It
  // measures in the page font size, not in the smaller size of the axis labels, so the widest
  // label of an axis that fits its labels exactly would break, for example "6 000 000" on 2 lines.
  const formatLabel = (value: number) => formatTick(value).replaceAll(' ', NON_BREAKING_SPACE);

  if (isNil(ticks)) {
    return {...chartStyle.yAxis, width: DEFAULT_AXIS_WIDTH, tickFormatter: formatLabel};
  }

  const labelWidth = measureWidestText(
    ticks.map(formatLabel),
    Number.parseFloat(theme.fontSizes.text.xSmall),
    theme.fonts.body
  );

  return {
    ...chartStyle.yAxis,
    ticks,
    domain: [ticks.at(0) ?? 0, ticks.at(-1) ?? 0],
    width: Math.ceil(labelWidth) + chartStyle.yAxis.tickMargin,
    tickFormatter: formatLabel,
  };
}
